const DB_NAME = 'rsaUploads';
const DB_VERSION = 1;
const STORE = 'sessions';

let _dbPromise = null;
let _unavailableLogged = false;

function logUnavailable(reason) {
  if (_unavailableLogged) return;
  _unavailableLogged = true;
  console.warn('[uploadSessionsDB] IndexedDB unavailable:', reason);
}

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      logUnavailable('indexedDB global is undefined');
      _dbPromise = null;
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'localId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      logUnavailable(req.error?.message || 'open failed');
      _dbPromise = null;
      reject(req.error || new Error('indexedDB open failed'));
    };
  });
  return _dbPromise;
}

async function withStore(mode, fn) {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      // fn may return {value: ...} (sync operations) or a Promise that resolves to the
      // result before tx.oncomplete fires. The outer await unwraps either shape.
      const result = fn(store);
      tx.oncomplete = () => resolve(result?.value !== undefined ? result.value : result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('tx aborted'));
    });
  } catch (err) {
    throw err;
  }
}

export async function saveSession(record) {
  return withStore('readwrite', (store) => {
    const now = Date.now();
    const payload = { ...record, updatedAt: now, createdAt: record.createdAt || now };
    store.put(payload);
    return { value: payload };
  });
}

export async function updateSession(localId, patch) {
  return withStore('readwrite', (store) => {
    return new Promise((resolve, reject) => {
      const getReq = store.get(localId);
      getReq.onsuccess = () => {
        const existing = getReq.result;
        if (!existing) { resolve(null); return; }
        const merged = { ...existing, ...patch, updatedAt: Date.now() };
        const putReq = store.put(merged);
        putReq.onsuccess = () => resolve(merged);
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  });
}

export async function deleteSession(localId) {
  return withStore('readwrite', (store) => {
    store.delete(localId);
  });
}

export async function getAllSessions() {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}
